import {
  DataGrid as MuiDataGrid,
  type DataGridProps,
  type GridActionsColDef,
  type GridColDef,
  type GridSingleSelectColDef,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import type { GridBaseColDef } from '@mui/x-data-grid/internals';

import { getColumnFilterOperators } from './getColumnOperators';
import { getColumnSize } from './getColumnSize';

const transformColumns = <T extends GridValidRowModel>(columns: DataGridColumn<T>[]): GridColDef<T>[] => {
  return columns.map((column) => {
    return {
      ...column,
      ...getColumnSize(column.columnSize),
      ...getColumnFilterOperators(column.type, column.filterOperators),
    };
  });
};

type ColumnBase<T extends GridValidRowModel> = GridBaseColDef<T> | GridActionsColDef<T> | GridSingleSelectColDef<T>;
export type DataGridColumn<T extends GridValidRowModel> = ColumnBase<T> & {
  columnSize?: 'small' | 'medium' | 'large' | 'xLarge';
};

export type Props<T extends GridValidRowModel> = Omit<DataGridProps, 'columns'> & {
  columns: DataGridColumn<T>[];
};
export const DataGrid = <T extends GridValidRowModel>(props: Props<T>) => {
  const columns = transformColumns<T>(props.columns);
  return (
    <MuiDataGrid<T>
      showColumnVerticalBorder
      showCellVerticalBorder
      autoHeight
      density="standard"
      sx={{
        '& .MuiDataGrid-row:hover': {
          cursor: 'pointer',
        },
        overflowX: 'scroll',
      }}
      {...props}
      columns={columns}
      disableVirtualization={process.env.NODE_ENV === 'test'}
    />
  );
};
